import { QConfigGeneratorExecutionStatus } from '@exam-management/graphql';
import { useParams } from '@exam-management/router';
import {
  Alert,
  Button,
  EllipsisText,
  Flex,
  OpenInNewOutlinedIcon,
  Paper,
  Text,
} from '@shared/components';

import {
  SummaryStatsGroup,
  ResultNoRowsOverlay,
  GeneratingOverlay,
  type QConfigGeneratorResult,
} from '../../components';
import { useQConfigGeneratorDetails } from '../../providers';
import { QConfigGeneratorResultList } from '../qconfig-generator-result-list';

function calculateFailedQConfigs(
  totalQConfigs: number | null,
  successfulQConfigs: number | null,
): number | null {
  if (
    totalQConfigs !== null &&
    successfulQConfigs !== null &&
    totalQConfigs - successfulQConfigs === 0
  ) {
    return 0;
  }
  if (totalQConfigs !== null && successfulQConfigs !== null) {
    return totalQConfigs - successfulQConfigs;
  }
  return null;
}

function getQConfigGeneratorOverlay(props: {
  qConfigStatus: QConfigGeneratorExecutionStatus;
  courseId: string;
  successfulQConfigs: number | null;
}) {
  const { qConfigStatus, courseId, successfulQConfigs } = props;
  if (
    qConfigStatus === QConfigGeneratorExecutionStatus.Pending ||
    qConfigStatus === QConfigGeneratorExecutionStatus.Processing
  ) {
    return <GeneratingOverlay courseId={courseId} />;
  }

  if (
    qConfigStatus === QConfigGeneratorExecutionStatus.Failed ||
    (successfulQConfigs === 0 && qConfigStatus === QConfigGeneratorExecutionStatus.Done)
  ) {
    return <ResultNoRowsOverlay courseId={courseId} />;
  }
}

export default function ResultSummarySection() {
  const { courseId } = useParams('/exam-courses/:courseId/qconfig-generators/:qConfigGeneratorId');
  const { qconfigGenerator } = useQConfigGeneratorDetails();
  const qConfigStatus = qconfigGenerator?.latestExecution.status;
  const totalQConfigs = qconfigGenerator?.needTotal ?? null;
  const successfulQConfigs = qconfigGenerator?.latestExecution.totalResults ?? null;
  const failedQConfigs = calculateFailedQConfigs(totalQConfigs, successfulQConfigs);
  const isStatusGenerating =
    qConfigStatus === QConfigGeneratorExecutionStatus.Pending ||
    qConfigStatus === QConfigGeneratorExecutionStatus.Processing;

  const overlay = getQConfigGeneratorOverlay({
    qConfigStatus,
    courseId,
    successfulQConfigs,
  });

  const rows: QConfigGeneratorResult[] = (qconfigGenerator?.latestExecution.results ?? [])
    .map((result) => {
      const qConfig = result.qConfig;

      return {
        id: result.id,
        code: qConfig ? String(qConfig.code) : null,
        isDeleted: !qConfig,
        name: qConfig?.name ? (
          <EllipsisText maxLines={2} variant='body1Semibold'>
            {qConfig.name}
          </EllipsisText>
        ) : (
          <Flex sx={{ gap: (theme) => theme.spacing(1), alignItems: 'center' }}>
            <EllipsisText variant='body1Semibold'>{qconfigGenerator?.name}</EllipsisText>
            <Text
              variant='body2'
              component='span'
              sx={{
                color: (theme) => theme.palette.text.secondary,
                minWidth: 'max-content',
              }}
            >
              (รายการนี้ถูกลบไปแล้ว)
            </Text>
          </Flex>
        ),
      };
    })
    .sort((a, b) => {
      if (a.isDeleted !== b.isDeleted) {
        return a.isDeleted ? 1 : -1;
      }
      if (a.code == null || b.code == null) {
        return a.code == null ? 1 : -1;
      }
      return a.code.localeCompare(b.code);
    })
    .map((row) => ({
      ...row,
      code: row.code ?? '-',
    }));

  return (
    <Paper>
      <Flex
        sx={(theme) => ({
          p: theme.spacing(6),
          flexDirection: 'column',
          gap: theme.spacing(6),
        })}
      >
        <SummaryStatsGroup
          totalQConfigs={totalQConfigs}
          successfulQConfigs={isStatusGenerating ? null : successfulQConfigs}
          failedQConfigs={isStatusGenerating ? null : failedQConfigs}
        />
        {qConfigStatus === QConfigGeneratorExecutionStatus.Done && successfulQConfigs > 0 ? (
          <>
            <Alert
              severity='info'
              sx={{
                '.MuiAlert-message': {
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                },
              }}
            >
              <Text variant='body2Semibold' sx={{ color: (theme) => theme.palette.info.dark }}>
                QConfig ที่ผ่านเกณฑ์สามารถดูรายละเอียดได้ที่หน้า QConfig
              </Text>
              <Button
                variant='text'
                onClick={() => window.open(`/exam-courses/${courseId}/qconfigs`)}
                endIcon={<OpenInNewOutlinedIcon />}
                sx={{ color: (theme) => theme.palette.info.dark }}
              >
                ไปที่ QConfig
              </Button>
            </Alert>
            <QConfigGeneratorResultList rows={rows} />
          </>
        ) : (
          overlay
        )}
      </Flex>
    </Paper>
  );
}
